import React, {useEffect, useState} from 'react';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Typography,
    Backdrop,
    CircularProgress,
    Button,
    Divider,
} from '@mui/material';

import {isShiftPlanAfterCurrentTime} from "../../helpers/ShiftPlanHelper";
import {hasSelectedTimeblocks, hasNewlySelectedTimeblocks} from "../../helpers/TimeblockHelper";
import useShiftPlanCardData from "../../hooks/useShiftPlanCardData";
import useShiftPlanActions from "../../hooks/useShiftPlanActions";
import {SHIFT_PLAN_TIMEBLOCK_WIDTH_REM} from "../../domain/ShiftPlanConstants";
import ShiftPlanTimeblockGrid from './ShiftPlanTimeblockGrid';
import PerformerSelect from './PerformerSelect';
import CustomAlert from '../CustomAlert';
import {BOOKABLE_STATUS} from '../../helpers/TimeblockHelper';

const visibleBlocks = 6;

const styles = {
    card: {
        borderRadius: '0.75rem',
        padding: '0.5rem',
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    cardContent: {
        padding: '0 0.5rem 0.5rem 0.5rem',
        flex: 1,
        overflow: 'auto'
    },
    dialog: {
        actions: {
            container: {
                justifyContent: 'space-between',
                paddingBottom: 0
            },
            buttonGroup: {
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }
        }
    },
    timeblock: {
        gridDimensions: {
            width: `${SHIFT_PLAN_TIMEBLOCK_WIDTH_REM + (SHIFT_PLAN_TIMEBLOCK_WIDTH_REM * visibleBlocks)}rem`,
            maxWidth: '100%'
        },
        emptyState: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '100%',
            minHeight: '12.5rem',
            border: '0.0625rem solid #ccc',
            width: `${SHIFT_PLAN_TIMEBLOCK_WIDTH_REM + (SHIFT_PLAN_TIMEBLOCK_WIDTH_REM * visibleBlocks)}rem`,
            maxWidth: '100%'
        }
    },
    bookableStatus: {
        selectBox: {
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '0.5rem',
        }
    },
    loadingBackdrop: {
        color: '#fff',
        display: 'flex',
        zIndex: 1500
    },
};

export default function ShiftPlanCard({currentDate: selectedDate, shouldRefreshShiftPlans, shiftPlanId, inputShiftPlans = [], isLoadingShiftPlans = false}) {

    const [shiftBookableStatuses, setShiftBookableStatuses] = useState({});

    const {
        availableServiceTypes,
        currentDate,
        deleteShiftPlanButtonEnabled,
        displayedShiftPlan,
        emptyGrid,
        firstShiftPlan,
        isProcessingShiftPlanData,
        performerEnrollments,
        setIsProcessingShiftPlanData,
        shiftPlans,
        visibleTimeBlocks
    } = useShiftPlanCardData({selectedDate, shiftPlanId, inputShiftPlans});

    const {
        alertMessage,
        handleCloseCustomAlert,
        handleDeleteShiftPlans,
        handleGenerateShifts,
        handlePerformerEnrollment,
        handleUpdateShifts,
        isEnrolling,
        isGeneratingShifts,
        isUnenrolling,
        isUpdatingShifts
    } = useShiftPlanActions({shiftPlans, shouldRefreshShiftPlans, setIsProcessingShiftPlanData, shiftBookableStatuses});

    useEffect(() => {
        if (!displayedShiftPlan?.id) {
            return;
        }

        setShiftBookableStatuses((prevState) => {
            if (prevState[displayedShiftPlan.id]) {
                return prevState;
            }

            return {
                ...prevState,
                [displayedShiftPlan.id]: displayedShiftPlan.bookableServiceTypePlans?.map(() => BOOKABLE_STATUS.BOOKABLE) || []
            };
        });
    }, [displayedShiftPlan]);

    const handleBookableServiceTypesChange = (updatedStatuses) => {
        if (!displayedShiftPlan?.id) {
            return;
        }

        setShiftBookableStatuses((prevState) => ({
            ...prevState,
            [displayedShiftPlan.id]: updatedStatuses
        }));
    };

    const hasShiftPlans = shiftPlans.length > 0;
    const hasEnrolledPerformers = performerEnrollments.length > 0;

    const isDataReady = !isProcessingShiftPlanData && !isLoadingShiftPlans;
    const hasNoShiftPlans = !hasShiftPlans;
    const hasVisibleTimeblocks = visibleTimeBlocks.length > 0;
    const hasShiftPlansButNoPerformers = hasShiftPlans && !hasEnrolledPerformers;
    const hasShiftPlansWithPerformers = hasShiftPlans && hasEnrolledPerformers;
    const displayLoadingBar = isProcessingShiftPlanData || isLoadingShiftPlans || isEnrolling || isUnenrolling || isGeneratingShifts || isUpdatingShifts;
    const isShiftPlanInPast = isShiftPlanAfterCurrentTime(firstShiftPlan?.startTime);

    const canGenerateShifts = hasShiftPlans && hasEnrolledPerformers && hasSelectedTimeblocks(shiftPlans);
    const canUpdateShifts = hasNewlySelectedTimeblocks(shiftPlans);

    return (
        <>
            <Card elevation={4} sx={styles.card}>
                <CardContent sx={styles.cardContent}>
                    {
                      isDataReady
                      &&
                      <>
                          {
                            hasNoShiftPlans
                            &&
                            (
                              <Box sx={styles.timeblock.emptyState}>
                                  <Typography variant="body1" color="text.secondary">
                                      No shift plans found
                                  </Typography>
                              </Box>
                            )}

                          {
                            hasShiftPlansButNoPerformers
                            &&
                            (
                              <ShiftPlanTimeblockGrid
                                performerEnrollments={emptyGrid.performers}
                                timeblocks={visibleTimeBlocks}
                                isReadOnly={true}
                                selectedDate={currentDate}
                                shiftPlan={displayedShiftPlan}
                                bookableServiceTypes={shiftBookableStatuses[displayedShiftPlan?.id] || []}
                                setBookableServiceTypes={handleBookableServiceTypesChange}/>
                            )}

                          {
                              hasShiftPlansWithPerformers
                              && hasVisibleTimeblocks
                              &&
                              <ShiftPlanTimeblockGrid
                                  performerEnrollments={performerEnrollments}
                                  timeblocks={visibleTimeBlocks}
                                  availableServiceTypes={availableServiceTypes}
                                  isReadOnly={false}
                                  selectedDate={currentDate}
                                  shiftPlan={displayedShiftPlan}
                                  bookableServiceTypes={shiftBookableStatuses[displayedShiftPlan?.id] || []}
                                  setBookableServiceTypes={handleBookableServiceTypesChange}
                              />
                          }
                      </>
                    }
                </CardContent>

                <Divider/>

                <CardActions sx={styles.dialog.actions.container}>
                    <Box sx={styles.dialog.actions.buttonGroup}>
                        {
                            isDataReady
                            &&
                            hasShiftPlans
                            &&
                            <PerformerSelect
                                shiftPlan={firstShiftPlan}
                                handlePerformerEnrollment={handlePerformerEnrollment}/>
                        }

                    </Box>
                    <Box
                        sx={styles.dialog.actions.buttonGroup}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleDeleteShiftPlans}
                            disabled={!deleteShiftPlanButtonEnabled}>
                            Delete
                        </Button>
                        <Box>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleUpdateShifts}
                                disabled={!canUpdateShifts || isUpdatingShifts || !isShiftPlanInPast}>
                                Update
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleGenerateShifts}
                                disabled={!canGenerateShifts || isGeneratingShifts || !isShiftPlanInPast}>
                                Generate
                            </Button>
                        </Box>
                    </Box>
                </CardActions>
            </Card>

            <Backdrop open={displayLoadingBar} sx={styles.loadingBackdrop}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
        </>
    );
}